/**
 * ResourcePickerPanel.jsx — §5 right-side slide-in resource picker.
 *
 * Reads from the same /api/assets data source as the Resources page.
 * Lets users attach files (added as direct attachments via onAttachFile)
 * or videos (formatted link block inserted via onInsertVideoLink).
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, X, Video as VideoIcon, FileText, File as FileIcon, Image as ImageIcon, GraduationCap } from "lucide-react";
import { API, useAuth } from "@/App";

const CATEGORY_OPTIONS = [
  { id: "all", label: "All" },
  { id: "documents", label: "Documents" },
  { id: "videos", label: "Videos" },
  { id: "files", label: "Files" },
  { id: "training", label: "Training" },
];

function classify(asset) {
  const name = (asset?.name || "").toLowerCase();
  const url = (asset?.url || asset?.thumbnail_url || "").toLowerCase();
  const tag = (asset?.category || "").toLowerCase();
  if (asset?.id?.startsWith?.("yt-") || /youtube\.com|youtu\.be/.test(url) || asset?.type === "video") return "videos";
  if (/training|course|module|lesson/.test(name + tag)) return "training";
  if (/\.(pdf|docx?|xlsx?|pptx?|csv|md|txt)$/i.test(name)) return "documents";
  return "files";
}

function formatSize(bytes) {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function iconFor(kind, asset) {
  if (kind === "videos") return <VideoIcon style={{ width: 14, height: 14, color: "#A32D2D" }} />;
  if (kind === "training") return <GraduationCap style={{ width: 14, height: 14, color: "#3C3489" }} />;
  if (kind === "documents") return <FileText style={{ width: 14, height: 14, color: "#185FA5" }} />;
  if (asset?.type === "image") return <ImageIcon style={{ width: 14, height: 14, color: "#888" }} />;
  return <FileIcon style={{ width: 14, height: 14, color: "#888" }} />;
}

export default function ResourcePickerPanel({ open, onClose, onAttachFile, onInsertVideoLink }) {
  const { token } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [category, setCategory] = useState("all");
  const [attached, setAttached] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/assets`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setAssets(Array.isArray(res.data) ? res.data : []);
      } catch (_e) {
        if (!cancelled) setAssets([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, token]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const filtered = useMemo(() => {
    const q = debounced.trim().toLowerCase();
    return assets.filter((a) => {
      const kind = classify(a);
      if (category !== "all" && kind !== category) return false;
      if (!q) return true;
      const hay = [(a.name || ""), (a.description || ""), (a.category || "")].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [assets, debounced, category]);

  if (!open) return null;

  const handleAttach = (asset) => {
    const kind = classify(asset);
    if (kind === "videos") {
      onInsertVideoLink?.({
        id: asset.id,
        title: asset.name || "Video",
        url: asset.url || asset.video_url || asset.thumbnail_url || "",
        provider: /youtube\.com|youtu\.be/.test(asset.url || "") ? "youtube" : "video",
      });
    } else {
      onAttachFile?.({
        id: asset.id,
        name: asset.name || "Resource",
        size: asset.size || null,
        content_type: asset.content_type || asset.type || "application/octet-stream",
        url: asset.url || asset.thumbnail_url || "",
        kind,
      });
    }
    setAttached((s) => new Set([...s, asset.id]));
  };

  return (
    <div data-testid="resource-picker-overlay" style={{ position: "fixed", inset: 0, zIndex: 1500 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.20)" }} />
      <div
        data-testid="resource-picker-panel"
        style={{
          position: "absolute", top: 0, right: 0, bottom: 0, width: 400,
          background: "#fff", borderLeft: "0.5px solid #eee",
          boxShadow: "-4px 0 12px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column",
        }}
      >
        <div style={{ padding: "14px 16px", borderBottom: "0.5px solid #eee", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 14, fontWeight: 500 }}>Attach from Resources</span>
          <button data-testid="resource-picker-close" onClick={onClose} style={{ background: "none", border: "none", color: "#888", fontSize: 18, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: "12px 14px 6px" }}>
          <div style={{ position: "relative" }}>
            <Search style={{ position: "absolute", top: "50%", left: 10, transform: "translateY(-50%)", width: 12, height: 12, color: "#aaa", pointerEvents: "none" }} />
            <input
              data-testid="resource-picker-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search documents, videos, files..."
              style={{ width: "100%", border: "0.5px solid #ddd", borderRadius: 8, padding: "8px 12px 8px 28px", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 6, overflowX: "auto", padding: "0 14px 10px" }}>
          {CATEGORY_OPTIONS.map((c) => {
            const sel = category === c.id;
            return (
              <button
                key={c.id}
                data-testid={`resource-picker-cat-${c.id}`}
                onClick={() => setCategory(c.id)}
                style={{
                  border: `0.5px solid ${sel ? "#B5D4F4" : "#ddd"}`,
                  background: sel ? "#E6F1FB" : "#fff",
                  color: sel ? "#185FA5" : "#888",
                  borderRadius: 20, padding: "3px 10px", fontSize: 11, cursor: "pointer", fontFamily: "inherit",
                  whiteSpace: "nowrap",
                }}
              >{c.label}</button>
            );
          })}
        </div>

        <div data-testid="resource-picker-list" style={{ flex: 1, overflowY: "auto" }}>
          {loading && <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: "#888" }}>Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: "#888" }}>No resources found.</div>
          )}
          {!loading && filtered.map((a) => {
            const kind = classify(a);
            const isAttached = attached.has(a.id);
            return (
              <div
                key={a.id}
                data-testid={`resource-row-${a.id}`}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "0.5px solid #eee", cursor: "default" }}
                onMouseOver={(e) => (e.currentTarget.style.background = "#f9f9f9")}
                onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div style={{ width: 18, display: "flex", justifyContent: "center" }}>{iconFor(kind, a)}</div>
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name || "Untitled"}</div>
                  <div style={{ fontSize: 11, color: "#888", lineHeight: 1.4 }}>
                    {kind} {a.size ? `· ${formatSize(a.size)}` : ""} {a.duration ? `· ${a.duration}` : ""}
                  </div>
                </div>
                <button
                  data-testid={`resource-attach-${a.id}`}
                  onClick={() => handleAttach(a)}
                  disabled={isAttached}
                  style={{
                    border: isAttached ? "none" : "0.5px solid #B5D4F4",
                    background: isAttached ? "#EAF3DE" : "#fff",
                    color: isAttached ? "#3B6D11" : "#185FA5",
                    borderRadius: 6, padding: "3px 10px", fontSize: 11, cursor: isAttached ? "default" : "pointer", fontFamily: "inherit",
                  }}
                >{isAttached ? "Attached ✓" : "Attach"}</button>
              </div>
            );
          })}
        </div>

        <div style={{ padding: 12, borderTop: "0.5px solid #eee" }}>
          <button
            data-testid="resource-picker-done"
            onClick={onClose}
            style={{ width: "100%", background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}
          >Done</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact "Attach from Resources" link — drop into compose footer toolbars.
 */
export function AttachFromResourcesLink({ onClick }) {
  return (
    <button
      data-testid="attach-from-resources-link"
      onClick={onClick}
      style={{ background: "none", border: "none", color: "#185FA5", fontSize: 11, cursor: "pointer", padding: 0 }}
    >📎 Attach from Resources</button>
  );
}

/**
 * Render attachment chips for a list of attached resources/files/videos.
 * Use below the message body in compose surfaces.
 */
export function AttachedResourcesRow({ files = [], videos = [], onRemoveFile, onRemoveVideo }) {
  if (files.length === 0 && videos.length === 0) return null;
  return (
    <div data-testid="attached-resources-row" style={{ marginTop: 8 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", color: "#888", letterSpacing: "0.3px", marginBottom: 4 }}>Attachments:</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {files.map((f, i) => (
          <span key={`f-${i}-${f.id || f.name}`} data-testid={`attached-file-${i}`} style={{ background: "#f5f5f5", border: "0.5px solid #eee", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 5 }}>
            <FileIcon style={{ width: 12, height: 12, color: "#888" }} />
            <span>{f.name}</span>
            <button onClick={() => onRemoveFile?.(i)} style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", padding: 0, fontSize: 12 }}>×</button>
          </span>
        ))}
        {videos.map((v, i) => (
          <span key={`v-${i}-${v.id || v.title}`} data-testid={`attached-video-${i}`} style={{ background: "#f5f5f5", border: "0.5px solid #eee", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 5 }}>
            <VideoIcon style={{ width: 12, height: 12, color: v.provider === "youtube" ? "#FF0000" : "#185FA5" }} />
            <span>{v.title}</span>
            <button onClick={() => onRemoveVideo?.(i)} style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", padding: 0, fontSize: 12 }}>×</button>
          </span>
        ))}
      </div>
    </div>
  );
}
